import { getLogger } from 'wanderu-logger';
import { areCookiesAvailable, isSessionStorageAvailable } from 'wanderu-utils';

import type { Cookies } from 'react-cookie';

const logger = getLogger('wanderu-discount');

const DISCOUNT_STORAGE_KEY = 'discount_data';
// Two writers, one contract. nexus's middleware (wanderu/nexus `src/proxy.ts`, spec
// `routing-middleware` §5) sets this same cookie, with these same name and attributes,
// when a promo link lands on a surface nexus serves (homepage, blog, /social, its C2C
// routes) — HandleRedirects never sees those requests. Both apps share one origin and
// one cookie jar, so the handoff works only because the two agree on these strings;
// nothing tests it in either repo. Renaming or narrowing here silently strands nexus's
// writes — mirror any change there. Values arrive percent-encoded from nexus
// (`encodeURIComponent`); universal-cookie decodes them on read.
const DISCOUNT_COOKIE_NAME = 'discountCode';
const COOKIE_OPTIONS = {
    path: '/',
    sameSite: true,
    secure: true,
};

export interface DiscountData {
    discountCode?: string;
}

const getDiscountDataFromCookies = (cookies: Cookies): DiscountData => {
    const discountCode = cookies.get(DISCOUNT_COOKIE_NAME);

    return {
        discountCode,
    };
};

const setDiscountDataCookie = (cookies: Cookies, {
    discountCode,
}: DiscountData) => {
    if (!areCookiesAvailable()) {
        return;
    }

    if (discountCode) {
        cookies.set(DISCOUNT_COOKIE_NAME, discountCode, COOKIE_OPTIONS);
    }
};

const clearDiscountDataCookie = (cookies: Cookies) => {
    if (!areCookiesAvailable()) {
        return;
    }

    cookies.remove(DISCOUNT_COOKIE_NAME, COOKIE_OPTIONS);
};

const getDiscountDataFromSessionStorage = (): DiscountData => {
    if (!isSessionStorageAvailable()) {
        return {};
    }

    const discountData = window.sessionStorage.getItem(DISCOUNT_STORAGE_KEY);

    if (!discountData) {
        return {};
    }

    try {
        const data = JSON.parse(discountData);
        return {
            discountCode: data?.discountCode,
        };
    } catch (e) {
        logger.warn('Unable to parse session storage value for discount data', e);
        return {};
    }
};

const setDiscountDataInSessionStorage = (data: DiscountData) => {
    if (!isSessionStorageAvailable()) {
        return undefined;
    }

    return window.sessionStorage.setItem(
        DISCOUNT_STORAGE_KEY,
        JSON.stringify(data),
    );
};

const initDiscountData = (cookies: Cookies): DiscountData => {
    if (!areCookiesAvailable()) {
        return getDiscountDataFromSessionStorage();
    }

    const { discountCode } = getDiscountDataFromCookies(cookies);
    // Clear the cookie immediately after reading — it's a one-way transport from HandleRedirects
    // (or from nexus's middleware, for a link that landed on a nexus-served page).
    // If left alive, any subsequent page load without the ?discountCode= param would re-read and
    // re-dispatch the stale code, even after the user removes it. sessionStorage (tab-scoped)
    // is the persistent store for the lifetime of the session.
    clearDiscountDataCookie(cookies);

    if (discountCode) {
        setDiscountDataInSessionStorage({
            discountCode,
        });

        return {
            discountCode,
        };
    }

    return getDiscountDataFromSessionStorage();
};

const clearDiscountDataFromSessionStorage = () => {
    if (!isSessionStorageAvailable()) {
        return;
    }

    window.sessionStorage.removeItem(DISCOUNT_STORAGE_KEY);
};

export {
    clearDiscountDataFromSessionStorage,
    getDiscountDataFromCookies,
    initDiscountData,
    setDiscountDataCookie,
};
